import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, ButtonGroup, FormControl, MenuItem, Select, Typography } from "@mui/material";
import {
  fetchError,
  getExportResult,
  getFitForWorkOfTeam,
  getOrgProgress,
  getTeamFitForWork,
  getTeamProgress,
  getTeamTraining,
  getTrainingOfTeam,
  requestDownloadProgressCSVForOrgTeam,
  requestDownloadProgressCSVForVirtualTeam,
} from "../../redux/actions";
import { theme } from "../../themes/theme";
import { VirtualTeam } from "../../types/models/Team";
import { AppState } from "../../redux/store";
import { TeamMembersState } from "../../types/models/TeamMembersState";
import AppInfoView from "../../components/AppBase/AppInfoView";
import AppLoader from "../../components/AppBase/AppLoader";
import YourTeamHeader from "./YourTeamHeader";
import MemberTable from "./MemberTable";
import EditTeam from "../all-teams/EditTeam";
import EmptyResult from "../rules/EmptyResult";
import { TeamMemberProgress } from "../../types/models/TeamMember";
import { useTeams } from "../../hooks/useTeams";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { grey } from "@mui/material/colors";
import { UserSkill } from "../../types/models/UserSkillCoverage";
import { UserFitnessStatus } from "../../types/models/UserFitnessStatus";
import { CSVExportStatusResponse } from "../../types/models/CSVExportStatusResponse";

export const MyTeamId = "my-team";

export default function YourTeamsPage() {
  const dispatch = useDispatch();
  const useTeamsHook = useTeams();
  const teamState = useSelector<AppState, TeamMembersState>((state) => state.teamMembers);

  const [loading, setLoading] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [selectedTeam, setSelectedTeam] = useState<VirtualTeam | undefined>(undefined);
  const [targetTeamProgress, setTargetTeamProgress] = useState<TeamMemberProgress[] | undefined>();
  const [displayTeamProgress, setDisplayTeamProgress] = useState<
    TeamMemberProgress[] | undefined
  >();
  const [displayAllTeamMember, setDisplayAllTeamMember] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentLimit, setCurrentLimit] = useState<number>(20);
  const [hasNext, setHasNext] = useState<boolean | undefined>(undefined);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [trainingRecord, setTrainingRecord] = useState<Record<string, UserSkill>>({});
  const [fitForWorkRecord, setFitForWorkRecord] = useState<Record<string, UserFitnessStatus>>({});
  const [ffwLoading, setFfwLoading] = useState<boolean>(false);
  const [trainingLoading, setTrainingLoading] = useState<boolean>(false);
  const csvLinkRef = useRef<unknown>(null);

  const getDataForDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);

    let requestResult: CSVExportStatusResponse | undefined = undefined;
    try {
      if (displayAllTeamMember) {
        // Call API to request csv data
        requestResult = await requestDownloadProgressCSVForOrgTeam();
      } else if (selectedTeam?.TeamId) {
        // Call API to request csv data for specific team
        requestResult = await requestDownloadProgressCSVForVirtualTeam(selectedTeam.TeamId);
      }
    } catch (error) {
      console.error("Failed to request CSV download:", error);
      setIsDownloading(false);
      dispatch(
        fetchError(
          "Failed to request CSV download: " + (error instanceof Error ? error.message : "")
        )
      );
      return;
    }

    const teamName = displayAllTeamMember ? "Org Team" : selectedTeam?.DisplayName || "";
    const pollingInterval = 5000; // 5 seconds
    const timer = setInterval(async () => {
      try {
        const result = await getExportResult(requestResult?.InstanceId ?? "", teamName);
        if (result.Status === "Complete") {
          clearInterval(timer);
          setIsDownloading(false);
        }
      } catch (err) {
        console.error("Polling stopped due to error:", err);
        clearInterval(timer);
        dispatch(fetchError("Failed to download CSV data."));
        setIsDownloading(false);
      }
    }, pollingInterval);
  };

  const getOrgProgressData = useCallback(() => {
    dispatch(
      getOrgProgress(currentPage, currentLimit, (progress, hasNextData, totalCountData) => {
        setHasNext(hasNextData);
        setTotalCount(totalCountData);
        setLoading(false);
        if (progress) {
          setTrainingLoading(true);
          dispatch(
            getTeamTraining(currentPage, currentLimit, (data) => {
              if (data) {
                setTrainingRecord(Object.fromEntries(data.map((data) => [data.UserId, data])));
              }
              setTrainingLoading(false);
            })
          );

          setFfwLoading(true);
          dispatch(
            getTeamFitForWork(currentPage, currentLimit, (data) => {
              if (data) {
                setFitForWorkRecord(Object.fromEntries(data?.map((data) => [data.UserId, data])));
              }
              setFfwLoading(false);
            })
          );
        }
      })
    );
  }, [dispatch, currentPage, currentLimit]);

  const getTeamData = useCallback(
    (teamId: string) => {
      if (!selectedTeam?.TeamId || selectedTeam.TeamId === MyTeamId) {
        setLoading(false);
        return;
      }

      dispatch(
        getTeamProgress(
          selectedTeam.TeamId,
          currentPage,
          currentLimit,
          (result, hasNextData, totalCountData) => {
            setHasNext(hasNextData);
            setTotalCount(totalCountData);
            setLoading(false);

            if (result) {
              setTrainingLoading(true);
              setFfwLoading(true);

              dispatch(
                getTrainingOfTeam(teamId, currentPage, currentLimit, (data) => {
                  if (data) {
                    setTrainingRecord(Object.fromEntries(data.map((data) => [data.UserId, data])));
                  }
                  setTrainingLoading(false);
                })
              );

              dispatch(
                getFitForWorkOfTeam(teamId, currentPage, currentLimit, (data) => {
                  if (data) {
                    setFitForWorkRecord(
                      Object.fromEntries(data.map((data) => [data.UserId, data]))
                    );
                  }
                  setFfwLoading(false);
                })
              );
            }
          }
        )
      );
    },
    [dispatch, currentLimit, currentPage, selectedTeam]
  );

  useEffect(() => {
    if (displayAllTeamMember) {
      setLoading(true);
      getOrgProgressData();
    }
  }, [displayAllTeamMember, currentPage, currentLimit]);

  useEffect(() => {
    if (selectedTeam?.TeamId && selectedTeam.TeamId !== MyTeamId && !displayAllTeamMember) {
      setLoading(true);
      getTeamData(selectedTeam.TeamId);
    }
  }, [selectedTeam?.TeamId, displayAllTeamMember, getTeamData]);

  useEffect(() => {
    if (teamState.teamProgressList && selectedTeam?.TeamId !== MyTeamId && !displayAllTeamMember) {
      setTargetTeamProgress(teamState.teamProgressList);
    }
  }, [teamState.teamProgressList, selectedTeam?.TeamId, displayAllTeamMember]);

  // const getHeaderActionElement = () => {
  //     if (isCurrentAccountOrgManager() && (selectedTeam && (selectedTeam.TeamId !== MyTeamId))) {
  //         return (
  //                 <Button
  //                     startIcon={<EditIcon />}
  //                     variant="text"
  //                     onClick={() => {
  //                         setOpenEditModal(true);
  //                     }}
  //                     sx={{ width: "130px", textTransform: "none", borderRadius: 1 }}>
  //                         Edit Team
  //                 </Button>
  //         );
  //     } else {
  //         return undefined;
  //     }
  // };

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AppLoader />
        </Box>
      )}
      <Box sx={{ backgroundColor: theme.page.bgColor, minHeight: "100vh" }}>
        <YourTeamHeader
          loading={loading}
          getDataForDownload={getDataForDownload}
          teamName={selectedTeam?.DisplayName}
          title="Team Members"
          // action={getHeaderActionElement()}
          onSelectTeam={(teamId) => {
            if (teamId === MyTeamId) {
              // Select "My Team"
              setDisplayAllTeamMember(true);
              setSelectedTeam(undefined);
            } else {
              setDisplayAllTeamMember(false);
              setSelectedTeam(teamState.myTeams?.find((team) => team.TeamId === teamId));
            }

            setTargetTeamProgress(undefined);
            setDisplayTeamProgress(undefined);

            setCurrentPage(0);
          }}
          isDownloading={isDownloading}
          csvLinkRef={csvLinkRef}
        />
        <Box sx={{ m: 2 }}>
          {!loading &&
            teamState.myTeams &&
            (selectedTeam || displayAllTeamMember ? (
              <MemberTable
                allMemberProgress={displayAllTeamMember}
                team={selectedTeam}
                onOpenEditDialog={() => setOpenEditModal(true)}
                displayTeamProgress={displayTeamProgress}
                setDisplayTeamProgress={setDisplayTeamProgress}
                targetTeamProgress={targetTeamProgress}
                setTargetTeamProgress={setTargetTeamProgress}
                useTeamsInstance={useTeamsHook}
                trainingRecord={trainingRecord}
                fitForWorkRecord={fitForWorkRecord}
                trainingLoading={trainingLoading}
                ffwLoading={ffwLoading}
              />
            ) : (
              <EmptyResult>No team selected</EmptyResult>
            ))}
        </Box>
        {totalCount && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px",
              visibility: loading ? "hidden" : "visible",
              margin: "0px 10px",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "14px", color: grey }}>
                {currentLimit * currentPage + 1} -{" "}
                {Math.min(currentLimit * (currentPage + 1), totalCount ?? 0)} of {totalCount} row(s)
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ marginRight: "10px" }}>Rows per page</Typography>
              <FormControl sx={{ marginRight: "10px" }} size="small">
                <Select
                  value={currentLimit}
                  onChange={(e) => setCurrentLimit(e.target.value as number)}
                >
                  {Array.from([10, 20, 30, 40, 50]).map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ButtonGroup variant="outlined">
                <Button size="small" disabled={currentPage === 0} onClick={() => setCurrentPage(0)}>
                  <KeyboardDoubleArrowLeftIcon />
                </Button>
                <Button
                  size="small"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <KeyboardArrowLeftIcon />
                </Button>
                <Button
                  size="small"
                  disabled={!hasNext}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  <KeyboardArrowRightIcon />
                </Button>
                <Button
                  size="small"
                  disabled={!hasNext}
                  onClick={() => setCurrentPage(Math.floor((totalCount ?? 0) / currentLimit))}
                >
                  <KeyboardDoubleArrowRightIcon />
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
        )}
      </Box>
      <EditTeam
        team={selectedTeam}
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        allUsers={teamState.users ?? []}
        setTargetTeamProgress={setTargetTeamProgress}
        isYourTeamsPage={true}
      ></EditTeam>
      <AppInfoView />
    </>
  );
}
